import React, { useCallback, useEffect, useRef, useState } from 'react';

import { useDispatch, useSelector } from 'react-redux';

import ChatIcon from 'assets/chat/chatIcon.svg';
import CloseIcon from 'assets/chat/close.svg';
import SearchIcon from 'assets/filters/searchIcon.svg';
import Button from 'common-ui/button';
import { formatUnreadCount } from 'common-ui/chat/constants';
import {
  getConversations,
  getMessages,
  getPlatforms,
  getUnreadCount,
} from 'features/chat/chatActions';
import {
  addIncomingMessage,
  selectConversations,
  selectConversationsLoading,
  selectMessages,
  selectMessagesLoading,
  selectMyEffectivePermissions,
  selectMyEffectivePermissionsLoading,
  selectPlatforms,
  selectPlatformsLoading,
  selectTotalUnreadCount,
} from 'features/chat/chatSlice';
import useDebounce from 'hooks/useDebounce';

import {
  BackArrow,
  ChatButton,
  ChatButtonNotification,
  ChatContainer,
  ChatModal,
  ChatModalBlock,
  ChatModalCLoseButtonIcon,
  ChatModalCloseButton,
  ChatModalHeader,
  ChatModalHeaderBackButton,
  ChatModalHeaderFlex,
  ChatModalHeaderIconBlock,
  ChatModalHeaderNotification,
  ChatModalHeaderNotificationDot,
  ChatModalHeaderNotificationNumber,
  ChatModalHeaderSubTitle,
  ChatModalHeaderTitle,
  ChatModalHeaderTitleBlock,
  ChatModalTitleIcon,
  ChatModalTopBorder,
  ChatSearch,
  SearchCloseButton,
  SearchIconWrapper,
  SearchInput,
  SearchInputContainer,
  SearchInputWrapper,
  ChatIcon as StyledChatIcon,
} from './Chat.styles';
import ChatBadges from './chatBadges';
import ChatList from './chatList';
// import ChatList from './chatList';
import ChatSingle from './chatSingle';
import PlatformAccountsSection from './platformAccountsSection';

const STORAGE_KEY = 'chat-button-position';
const BUTTON_SIZE = 56;
const MODAL_WIDTH = 537;
const MODAL_MIN_HEIGHT = 490;
const CONTAINER_GAP = 16;
const TOP_MARGIN = 170;

const getInitialPosition = () => {
  try {
    const saved = localStorage.getItem(STORAGE_KEY);
    if (saved) {
      return JSON.parse(saved);
    }
  } catch {
    // errors
  }
  return { right: 24, bottom: 24 };
};

const getModalWidth = () => {
  if (window.innerWidth <= 341) {
    return 280;
  } else if (window.innerWidth <= 396) {
    return 340;
  } else if (window.innerWidth <= 558) {
    return 380;
  }
  return MODAL_WIDTH;
};

const Chat = () => {
  const dispatch = useDispatch();
  const myPermissions = useSelector(selectMyEffectivePermissions);
  const permissionsLoading = useSelector(selectMyEffectivePermissionsLoading);
  const platforms = useSelector(selectPlatforms);
  const platformsLoading = useSelector(selectPlatformsLoading);
  const conversations = useSelector(selectConversations);
  const conversationsLoading = useSelector(selectConversationsLoading);
  const messages = useSelector(selectMessages);
  const messagesLoading = useSelector(selectMessagesLoading);

  const [isOpen, setIsOpen] = useState(false);
  const [position, setPosition] = useState(getInitialPosition);
  const [isDragging, setIsDragging] = useState(false);
  const [selectedPlatform, setSelectedPlatform] = useState(null);
  const [selectedConversation, setSelectedConversation] = useState(null);
  const [view, setView] = useState('list'); // 'list' or 'single'
  const [isSearchOpen, setIsSearchOpen] = useState(false);
  const [searchValue, setSearchValue] = useState('');
  const dragStartRef = useRef({ x: 0, y: 0, right: 0, bottom: 0 });
  const hasDraggedRef = useRef(false);
  const searchInputRef = useRef(null);
  const debouncedSearchValue = useDebounce(searchValue, 500);

  const handlePlatformSelect = (platform) => {
    // Check if user has access to this platform
    if (!myPermissions?.permissions) return;

    const hasAccess = myPermissions.permissions.some(
      (perm) => perm.platform === platform && !perm.isDenied
    );

    if (!hasAccess) {
      console.warn(`User does not have access to platform: ${platform}`);
      return;
    }

    if (selectedPlatform === platform) {
      return;
    }

    setSelectedPlatform(platform);
    setSelectedConversation(null);
    setView('list');
    setIsSearchOpen(false);
    setSearchValue('');
    dispatch(getConversations({ platform, search: '' }));
  };

  const handleConversationSelect = (conversation) => {
    setSelectedConversation(conversation);
    setView('single');
    setIsSearchOpen(false);
    setSearchValue('');
    dispatch(
      getMessages({ conversationId: conversation.conversationId, limit: 10, offset: 0 })
    );
  };

  const handleBackToList = () => {
    setSelectedConversation(null);
    setView('list');
    setIsSearchOpen(false);
    setSearchValue('');
    // Refresh platforms to update unread counts
    dispatch(getPlatforms());
    // Refresh total unread count
    dispatch(getUnreadCount());
    // Refresh conversations to update last read status
    if (selectedPlatform) {
      dispatch(getConversations({ platform: selectedPlatform, search: '' }));
    }
  };

  const handleSearchOpen = () => {
    setIsSearchOpen(true);
    setTimeout(() => {
      searchInputRef.current?.focus();
    }, 100);
  };

  const handleSearchClose = () => {
    setIsSearchOpen(false);
    setSearchValue('');
  };

  const handleSearchChange = (e) => {
    setSearchValue(e.target.value);
  };

  useEffect(() => {
    if (selectedPlatform && isOpen && view === 'list') {
      dispatch(getConversations({ platform: selectedPlatform, search: debouncedSearchValue }));
    }
  }, [debouncedSearchValue, selectedPlatform, isOpen, view, dispatch]);

  // Listen for real-time messages from Header.jsx socket via custom event
  useEffect(() => {
    if (!isOpen) {
      return;
    }

    // Message handler
    const handleMessage = (data) => {
      // Extract message from wrapper (socket sends { message: {...}, timestamp: "..." })
      const message = data?.message || data;

      if (!message) {
        return;
      }

      if (!message.conversationId) {
        return;
      }

      // If viewing this conversation, add the message immediately
      // Use loose comparison (==) to handle string vs number conversationId
      if (selectedConversation?.conversationId == message.conversationId) {
        dispatch(addIncomingMessage(message));
      } else {
        //else
      }

      // Refresh conversation list to update last message and unread counts
      if (selectedPlatform) {
        dispatch(
          getConversations({ platform: selectedPlatform, search: debouncedSearchValue })
        );
      }

      // Refresh platform counts to update badge
      dispatch(getPlatforms());

      // Refresh total unread count
      dispatch(getUnreadCount());
    };

    // Listen for messages from Header.jsx socket via custom event
    const handleCustomEvent = (event) => {
      // console.log('🔔 Chat.jsx received custom event from Header.jsx socket');
      handleMessage(event.detail);
    };

    window.addEventListener('chat-new-message', handleCustomEvent);
    // console.log('✅ Chat.jsx listening for custom events');

    return () => {
      window.removeEventListener('chat-new-message', handleCustomEvent);
    };
  }, [
    isOpen,
    selectedConversation?.conversationId,
    selectedPlatform,
    debouncedSearchValue,
    dispatch,
  ]);

  const hasAnyChatAccess = () => {
    if (!myPermissions?.permissions) return false;

    const VALID_PLATFORMS = ['facebook', 'instagram', 'whatsapp', 'imap'];

    return myPermissions.permissions.some(
      (perm) => !perm.isDenied && VALID_PLATFORMS.includes(perm.platform)
    );
  };

  // Get allowed platforms helper
  const getAllowedPlatforms = () => {
    if (!myPermissions?.permissions) return [];

    const VALID_PLATFORMS = ['facebook', 'instagram', 'whatsapp', 'imap'];

    return myPermissions.permissions
      .filter((perm) => !perm.isDenied && VALID_PLATFORMS.includes(perm.platform))
      .map((perm) => perm.platform);
  };

  const getPermissionTypeForPlatform = (platform) => {
    if (!myPermissions?.permissions || !platform) return null;

    const permission = myPermissions.permissions.find(
      (perm) => perm.platform === platform && !perm.isDenied
    );

    return permission?.type || null;
  };

  // Auto-select Messenger (facebook) as default when permissions load
  useEffect(() => {
    if (isOpen && myPermissions?.permissions && !selectedPlatform) {
      const allowedPlatforms = getAllowedPlatforms();

      // Prefer facebook (Messenger) as default, otherwise first available
      if (allowedPlatforms.includes('facebook')) {
        handlePlatformSelect('facebook');
      } else if (allowedPlatforms.length > 0) {
        handlePlatformSelect(allowedPlatforms[0]);
      }
    }
  }, [isOpen, myPermissions, selectedPlatform]);

  const handleDragStart = useCallback(
    (clientX, clientY) => {
      setIsDragging(true);
      hasDraggedRef.current = false;
      dragStartRef.current = {
        x: clientX,
        y: clientY,
        right: position.right,
        bottom: position.bottom,
      };
    },
    [position]
  );

  const handleDragMove = useCallback(
    (clientX, clientY) => {
      if (!isDragging) return;

      const deltaX = dragStartRef.current.x - clientX;
      const deltaY = dragStartRef.current.y - clientY;

      if (Math.abs(deltaX) > 5 || Math.abs(deltaY) > 5) {
        hasDraggedRef.current = true;
      }

      const modalWidth = getModalWidth();
      const maxRight = isOpen
        ? window.innerWidth - modalWidth
        : window.innerWidth - BUTTON_SIZE;

      const maxBottom = isOpen
        ? window.innerHeight - (MODAL_MIN_HEIGHT + CONTAINER_GAP + BUTTON_SIZE + TOP_MARGIN)
        : window.innerHeight - BUTTON_SIZE;

      const newRight = Math.max(0, Math.min(maxRight, dragStartRef.current.right + deltaX));
      const newBottom = Math.max(0, Math.min(maxBottom, dragStartRef.current.bottom + deltaY));

      setPosition({ right: newRight, bottom: newBottom });
    },
    [isDragging, isOpen]
  );

  const handleDragEnd = useCallback(() => {
    if (isDragging) {
      setIsDragging(false);
      localStorage.setItem(STORAGE_KEY, JSON.stringify(position));
    }
  }, [isDragging, position]);

  const handleMouseDown = (e) => {
    e.preventDefault();
    handleDragStart(e.clientX, e.clientY);
  };

  const handleTouchStart = (e) => {
    const touch = e.touches[0];
    handleDragStart(touch.clientX, touch.clientY);
  };

  useEffect(() => {
    const handleMouseMove = (e) => handleDragMove(e.clientX, e.clientY);
    const handleTouchMove = (e) => {
      const touch = e.touches[0];
      handleDragMove(touch.clientX, touch.clientY);
    };
    const handleMouseUp = () => handleDragEnd();
    const handleTouchEnd = () => handleDragEnd();

    if (isDragging) {
      document.addEventListener('mousemove', handleMouseMove);
      document.addEventListener('mouseup', handleMouseUp);
      document.addEventListener('touchmove', handleTouchMove);
      document.addEventListener('touchend', handleTouchEnd);
    }

    return () => {
      document.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseup', handleMouseUp);
      document.removeEventListener('touchmove', handleTouchMove);
      document.removeEventListener('touchend', handleTouchEnd);
    };
  }, [isDragging, handleDragMove, handleDragEnd]);

  // TODO: Uncomment this logic when needed - Listen for custom event to open WhatsApp chat with a specific phone number
  // useEffect(() => {
  //   const handleOpenWhatsAppChat = async (event) => {
  //     const { phoneNumber, customerName, customerId, platform } = event.detail;
  //     console.log('Opening WhatsApp chat for:', { phoneNumber, customerName, customerId });

  //     if (!isOpen) {
  //       setIsOpen(true);
  //       await dispatch(getMyEffectivePermissions());
  //       await dispatch(getPlatforms());
  //       await dispatch(getUnreadCount());
  //     }

  //     setSelectedPlatform('whatsapp');

  //     const conversationsResult = await dispatch(getConversations({ platform: 'whatsapp' }));

  //     if (conversationsResult.payload?.conversations) {
  //       const existingConversation = conversationsResult.payload.conversations.find((conv) => {
  //         const normalizedPhone = phoneNumber.replace(/\D/g, '');
  //         const convPhone = conv.participantPhone?.replace(/\D/g, '') || '';
  //         return convPhone.includes(normalizedPhone) || normalizedPhone.includes(convPhone);
  //       });

  //       if (existingConversation) {
  //         console.log('Found existing WhatsApp conversation:', existingConversation);
  //         setSelectedConversation(existingConversation);
  //         setView('single');
  //         dispatch(getMessages({ conversationId: existingConversation.conversationId }));
  //       } else {
  //         console.log('No existing WhatsApp conversation found for:', phoneNumber);
  //         setView('list');
  //       }
  //     }
  //   };

  //   window.addEventListener('open-whatsapp-chat', handleOpenWhatsAppChat);

  //   return () => {
  //     window.removeEventListener('open-whatsapp-chat', handleOpenWhatsAppChat);
  //   };
  // }, [isOpen, dispatch]);
  useEffect(() => {
    if (isOpen) {
      const modalWidth = getModalWidth();
      const maxRight = window.innerWidth - modalWidth;
      const maxBottom =
        window.innerHeight - (MODAL_MIN_HEIGHT + CONTAINER_GAP + BUTTON_SIZE + TOP_MARGIN);

      const adjustedRight = Math.max(0, Math.min(maxRight, position.right));
      const adjustedBottom = Math.max(0, Math.min(maxBottom, position.bottom));

      if (adjustedRight !== position.right || adjustedBottom !== position.bottom) {
        setPosition({ right: adjustedRight, bottom: adjustedBottom });
        localStorage.setItem(
          STORAGE_KEY,
          JSON.stringify({ right: adjustedRight, bottom: adjustedBottom })
        );
      }
    }
  }, [isOpen, position.right, position.bottom]);

  useEffect(() => {
    const handleResize = () => {
      if (isOpen) {
        const modalWidth = getModalWidth();
        const maxRight = window.innerWidth - modalWidth;
        const maxBottom =
          window.innerHeight - (MODAL_MIN_HEIGHT + CONTAINER_GAP + BUTTON_SIZE + TOP_MARGIN);

        const adjustedRight = Math.max(0, Math.min(maxRight, position.right));
        const adjustedBottom = Math.max(0, Math.min(maxBottom, position.bottom));

        if (adjustedRight !== position.right || adjustedBottom !== position.bottom) {
          setPosition({ right: adjustedRight, bottom: adjustedBottom });
        }
      } else {
        const maxRight = window.innerWidth - BUTTON_SIZE;
        const maxBottom = window.innerHeight - BUTTON_SIZE;

        const adjustedRight = Math.max(0, Math.min(maxRight, position.right));
        const adjustedBottom = Math.max(0, Math.min(maxBottom, position.bottom));

        if (adjustedRight !== position.right || adjustedBottom !== position.bottom) {
          setPosition({ right: adjustedRight, bottom: adjustedBottom });
        }
      }
    };

    window.addEventListener('resize', handleResize);
    return () => window.removeEventListener('resize', handleResize);
  }, [isOpen, position.right, position.bottom]);

  const handleButtonClick = () => {
    if (!hasDraggedRef.current) {
      const willOpen = !isOpen;
      setIsOpen(willOpen);
      if (willOpen) {
        dispatch(getPlatforms());
        dispatch(getUnreadCount());
      }
    }
  };

  // Get total unread count from Redux state
  const totalUnreadCount = useSelector(selectTotalUnreadCount);

  if (!permissionsLoading && myPermissions && !hasAnyChatAccess()) {
    return null;
  }

  return (
    <ChatContainer style={{ right: position.right, bottom: position.bottom }}>
      {isOpen && (
        <ChatModalBlock>
          <ChatModalCloseButton
            onClick={() => {
              setIsOpen(false);
              setIsSearchOpen(false);
              setSearchValue('');
              // Refresh platforms and unread counts when closing
              dispatch(getPlatforms());
              dispatch(getUnreadCount());
            }}
          >
            <ChatModalCLoseButtonIcon src={CloseIcon} />
          </ChatModalCloseButton>
          <ChatModal>
            <ChatModalTopBorder></ChatModalTopBorder>
            <ChatModalHeader>
              {view === 'single' && (
                <ChatModalHeaderBackButton onClick={handleBackToList}>
                  <BackArrow>&lt;</BackArrow>
                </ChatModalHeaderBackButton>
              )}
              <ChatModalHeaderIconBlock>
                <ChatModalTitleIcon src={ChatIcon} />
              </ChatModalHeaderIconBlock>
              <ChatModalHeaderTitleBlock>
                <ChatModalHeaderFlex>
                  <ChatModalHeaderTitle>Unified Messaging</ChatModalHeaderTitle>
                  <ChatModalHeaderSubTitle>All platform conversations</ChatModalHeaderSubTitle>
                </ChatModalHeaderFlex>
                <ChatModalHeaderNotification>
                  <ChatModalHeaderNotificationDot></ChatModalHeaderNotificationDot>
                  <ChatModalHeaderNotificationNumber>
                    {formatUnreadCount(totalUnreadCount)}
                  </ChatModalHeaderNotificationNumber>
                </ChatModalHeaderNotification>
              </ChatModalHeaderTitleBlock>
            </ChatModalHeader>
            {view === 'single' && (
              <PlatformAccountsSection
                platforms={platforms}
                permissions={myPermissions}
                conversations={conversations}
                selectedPlatform={selectedPlatform}
                onPlatformSelect={handlePlatformSelect}
                onUserClick={handleConversationSelect}
              />
            )}
            {view === 'list' && (
              <>
                <ChatBadges
                  permissions={myPermissions}
                  platforms={platforms}
                  loading={permissionsLoading || platformsLoading}
                  selectedPlatform={selectedPlatform}
                  onPlatformSelect={handlePlatformSelect}
                />
                <ChatSearch>
                  {!isSearchOpen ? (
                    <Button
                      width={86}
                      outlined
                      className="action-button"
                      variant="search"
                      onClick={handleSearchOpen}
                    >
                      Search
                    </Button>
                  ) : (
                    <SearchInputContainer>
                      <SearchInputWrapper>
                        <SearchIconWrapper>
                          <img src={SearchIcon} alt="Search" />
                        </SearchIconWrapper>
                        <SearchInput
                          ref={searchInputRef}
                          type="text"
                          placeholder="Search"
                          value={searchValue}
                          onChange={handleSearchChange}
                        />
                        <SearchCloseButton onClick={handleSearchClose}>
                          <img src={CloseIcon} alt="Close search" />
                        </SearchCloseButton>
                      </SearchInputWrapper>
                    </SearchInputContainer>
                  )}
                </ChatSearch>
                <ChatList
                  conversations={conversations}
                  loading={conversationsLoading}
                  onConversationSelect={handleConversationSelect}
                />
              </>
            )}
            {view === 'single' && (
              <ChatSingle
                conversation={selectedConversation}
                messages={messages}
                loading={messagesLoading}
                platform={selectedPlatform}
                permissionType={getPermissionTypeForPlatform(selectedPlatform)}
              />
            )}
          </ChatModal>
        </ChatModalBlock>
      )}
      <ChatButton
        $isDragging={isDragging}
        $isOpen={isOpen}
        onMouseDown={handleMouseDown}
        onTouchStart={handleTouchStart}
        onClick={handleButtonClick}
      >
        <StyledChatIcon src={ChatIcon} />
        {totalUnreadCount > 0 && (
          <ChatButtonNotification>
            {formatUnreadCount(totalUnreadCount)}
          </ChatButtonNotification>
        )}
      </ChatButton>
    </ChatContainer>
  );
};

export default Chat;
